import { CellModel, ColumnSource, gridConstants, RequestFilters, RowSource } from "@omegagrid/grid";
import { ColorTypes, actions } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { JournalEntity } from "@reporting/rp-types/accounting";

const PAGE_SIZE = 500;
const COMPACT_PAGE_SIZE = 50;

export class JournalGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private treeMode: boolean = false;

	constructor(
		private idGroup: number = null,
		private compact: boolean = false,
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		if (this.compact) {
			this._columns = [{
				data: 'date',
				header: msg('Date'),
				t: 'dt',
				w: 78,
				s: { alignment: { wrapText: true, vertical: 'center', horizontal: 'center' } },
			}, {
				data: 'procedure_name',
				header: msg('Procedure'),
				t: 't',
				w: 250,
				s: { alignment: { wrapText: true, vertical: 'center' } },
			}, {
				data: 'remark',
				header: msg('Text'),
				t: 'h',
				w: 400,
				s: { alignment: { wrapText: true, vertical: 'top' } },
			}];
			return this._columns;
		}
		this._columns = [{
			data: 'accounting',
			header: '#',
			t: 't',
			w: 100,
		}, {
			data: 'company',
			header: msg('Company'),
			t: 't',
			w: 200,
		}, {
			data: 'server',
			header: msg('Server'),
			t: 't',
			w: 65,
			hidden: !!this.idGroup
		}, {
			data: 'id_audit',
			header: '#',
			t: 'n',
			w: 50,
		}, {
			data: 'job_name',
			header: msg('Job'),
			t: 't',
			w: 100,
		}, {
			data: 'module',
			header: msg('Module'),
			t: 't',
			w: 100,
		}, {
			data: 'procedure_name',
			header: msg('Procedure'),
			t: 't',
			w: 100,
		}, {
			data: 'table_name',
			header: msg('Table'),
			t: 't',
			w: 100,
		}, {
			data: 'date',
			header: msg('Date'),
			t: 'dt',
			w: 145,
		}, {
			data: 'remark',
			header: msg('Text'),
			t: 'h',
			w: 450,
		}, {
			data: 'run_time',
			header: msg('Execution time'),
			t: 'n',
			w: 120,
		}, {
			data: 'count',
			header: msg('Count'),
			t: 'n',
			w: 70,
		}, {
			data: 'size',
			header: msg('Size'),
			t: 'n',
			w: 70,
		}, {
			data: 'error',
			header: msg('Error'),
			t: 'b',
			w: 65,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const params = this.createRequestParams(filters, {
			count: this.compact ? COMPACT_PAGE_SIZE : PAGE_SIZE,
		});
		// the compact view shows the current company only (the group-wide list stays in the admin module)
		if (this.compact) {
			const where = typeof params.where === 'object' ? params.where : {};
			params.where = { ...where, accounting: rp.accountingProvider.a };
			params.fields = ['date', 'module', 'procedure_name', 'remark', 'error'];
		}
 
		let journal;
		if (this.treeMode) {
			journal = await rp.ms('accounting').get<JournalEntity[]>(`journalTree${this.idGroup ? `/${this.idGroup}` : ''}`, params);
		} else {
			journal = await rp.ms('accounting').get<JournalEntity[]>(`journal${this.idGroup ? `/${this.idGroup}` : ''}`, params);
		}
		return journal.map(data => ({
			l: this.treeMode ? data.level : 0,
			h: gridConstants.ROW_HEIGHT * (this.compact ? 3 : 1),
			data: {
				...data,
				procedure_name: this.compact ? [data.module, data.procedure_name].filter(Boolean).join('/') : data.procedure_name,
				remark: (data.timespan && data.level < 2) ? data.timespan : this.formatRemark(data.remark, data.procedure_name, data.error),
				run_time: { v: data.run_time, dv: this.formatTime(data.run_time) },
				count: data.procedure_name?.toLowerCase() === 'upload' ? { v: data.count, dv: this.formatSize(data.count) } : data.count,
				size: { v: data.size, dv: this.formatSize(data.size) },
			}
		}));
	}

	formatRemark(remark: string, procedureName: string = null, error: number = 0): string {
		if (!remark) return '';
		let icon = '';
		const remarkLower = remark.toLowerCase();

		if ((procedureName ?? '').toLowerCase() === 'upload') {
			icon = `<og-icon icon="cloud-arrow-up" style="color: #0094FF"></og-icon>&nbsp;`;
			remark = remark.replace(/not modified/gi, '<span style="color: #CEB900">not modified</span>');
		} else if (remarkLower.startsWith('start')) {
			icon = `<og-icon icon="play" style="color: green"></og-icon>&nbsp;`;
		} else if (remarkLower.startsWith('stop')) {
			icon = `<og-icon icon="stop" style="color: red"></og-icon>&nbsp;`;
		} else if (remarkLower.startsWith('insert')) {
			icon = `<og-icon icon="plus" style="color: #CEB900"></og-icon>&nbsp;`;
		} else if (remarkLower.startsWith('delete')) {
			icon = `<og-icon icon="trash" style="color: #C40000"></og-icon>&nbsp;`;
		} else if (remarkLower.startsWith('info')) {
			icon = `<og-icon icon="circle-info" style="color: #0094FF"></og-icon>&nbsp;`;
		} else if (remarkLower.startsWith('merge')) {
			icon = `<og-icon icon="code-fork" style="color: #0094FF"></og-icon>&nbsp;`;
		}

		const colorStyle = error > 0 ? 'color: red' : '';
		return `${icon}<span style="${colorStyle}">${remark}</span>`;
	}

	formatTime(val: number): string {
		if (val === null || val === undefined) return '';
		const hours = Math.floor(val / 3600);
		const minutes = Math.floor((val % 3600) / 60);
		const seconds = val % 60;
		return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
	}

	formatSize(val: number): string {
		if (val === null || val === undefined) return '';
		if (val === 0) return `${val}`;
		if (val < 1024) return `${val} B`;
		if (val < 1024 * 1024) return `${(val / 1024).toFixed(2)} KB`;
		if (val < 1024 * 1024 * 1024) return `${(val / (1024 * 1024)).toFixed(2)} MB`;
		return `${(val / (1024 * 1024 * 1024)).toFixed(2)} GB`;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			pageSize: this.compact ? COMPACT_PAGE_SIZE : PAGE_SIZE,
			autoUpdate: this.compact,
			colLetters: false,
			columnSorting: true,
			filters: true,
			editable: false,
			rowTree: true,
			lightTheme: null,
			darkTheme: null,
			toolbar: {
				autoUpdate: { enabled: true },
				save: { enabled: false },
				discard: { enabled: false },
				new: { enabled: false },
			},
			customToolbar: this.compact ? [] : [{
				type: 'togglebutton',
				id: 'treeBtn',
				icon: { icon: 'list-tree' },
				text: msg('Tree'),
				color: ColorTypes.Accent,
			}],
		});
	}

	async onAction(action: string, cell: CellModel, event: actions.ActionEvent = null) {
		switch (action) {
			case 'treeBtn':
				this.treeMode = event.action.value as boolean;
				this.editor.refresh();
				break;
		}
	}

}
